import type { Heightfield, TerrainGenerationParams, TerrainProjectState } from './TerrainTypes';

/**
 * Deterministic procedural terrain generation (spec §6.2). Uses integer-hashed value noise with
 * fractal Brownian motion so that identical parameters always yield an identical heightfield — no
 * `Math.random`, no time, no floating-point seeding surprises.
 */

/** 32-bit integer hash → float in [0, 1). Pure and deterministic. */
function hash2(x: number, y: number, seed: number): number {
  let h = (x | 0) * 374761393 + (y | 0) * 668265263 + (seed | 0) * 2246822519;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}

/** Smootherstep fade for interpolation. */
function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Bilinear value noise on an integer lattice, seeded. Output in [0, 1). */
function valueNoise2D(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const v00 = hash2(xi, yi, seed);
  const v10 = hash2(xi + 1, yi, seed);
  const v01 = hash2(xi, yi + 1, seed);
  const v11 = hash2(xi + 1, yi + 1, seed);
  const u = fade(xf);
  const v = fade(yf);
  return lerp(lerp(v00, v10, u), lerp(v01, v11, u), v);
}

/** Fractal Brownian motion (summed octaves). Output roughly in [0, 1]. */
function fbm(x: number, y: number, seed: number, octaves: number, persistence: number): number {
  let total = 0;
  let amplitude = 1;
  let frequency = 1;
  let max = 0;
  for (let o = 0; o < octaves; o += 1) {
    total += valueNoise2D(x * frequency, y * frequency, seed + o * 1013) * amplitude;
    max += amplitude;
    amplitude *= persistence;
    frequency *= 2;
  }
  return max > 0 ? total / max : 0;
}

/**
 * Per-template base elevation contribution, independent of noise. `u`,`v` are normalized grid
 * coordinates in [0, 1].
 */
function templateBase(params: TerrainGenerationParams, u: number, v: number): number {
  switch (params.templateId) {
    case 'gentle-slope': {
      // Consistent slope along slopeDirection.
      const dir = params.slopeDirection;
      const along = Math.cos(dir) * (u - 0.5) + Math.sin(dir) * (v - 0.5);
      return along * params.slopeRiseMeters;
    }
    case 'natural-clearing': {
      // Bowl-ish clearing: gently raised perimeter, flatter center.
      const dx = u - 0.5;
      const dz = v - 0.5;
      const r = Math.sqrt(dx * dx + dz * dz) * 2; // 0 center → ~1.41 corner
      return Math.max(0, r - 0.5) * params.elevationAmplitude * 0.6;
    }
    default:
      return 0;
  }
}

/** Noise amplitude multiplier per template. */
function templateNoiseAmplitude(params: TerrainGenerationParams): number {
  switch (params.templateId) {
    case 'flat-residential':
      return params.elevationAmplitude * 0.08;
    case 'gentle-slope':
      return params.elevationAmplitude * 0.12;
    case 'rolling-landscape':
      return params.elevationAmplitude;
    case 'natural-clearing':
      return params.elevationAmplitude * 0.5;
    case 'blank-custom':
      return params.elevationAmplitude;
  }
}

/** Generate a deterministic heightfield from parameters. */
export function generateHeightfield(params: TerrainGenerationParams): Heightfield {
  const cols = params.segments + 1;
  const rows = params.segments + 1;
  const heights = new Float32Array(cols * rows);

  const octaves = 4;
  const persistence = 0.5;
  // Roughness scales the noise spatial frequency (more roughness = more hills).
  const noiseScale = 1 + params.roughness * 6;
  const noiseAmp = templateNoiseAmplitude(params);

  for (let r = 0; r < rows; r += 1) {
    const v = rows > 1 ? r / (rows - 1) : 0;
    for (let c = 0; c < cols; c += 1) {
      const u = cols > 1 ? c / (cols - 1) : 0;
      const base = templateBase(params, u, v);
      const n = fbm(u * noiseScale, v * noiseScale, params.seed, octaves, persistence);
      // Center noise around 0 so it varies both up and down.
      const noise = (n - 0.5) * 2 * noiseAmp;
      heights[r * cols + c] = base + noise;
    }
  }

  return {
    cols,
    rows,
    widthMeters: params.widthMeters,
    lengthMeters: params.lengthMeters,
    heights,
  };
}

/**
 * How far a stored height may sit from the generated one and still count as untouched.
 *
 * `bakedHeights` are written through `Float32Array` and rounded on the way to a file, so a site that
 * has never been sculpted still comes back a few ten-thousandths off. A millimetre is below what the
 * sculpt brush can produce and well above that noise.
 */
const GRADE_EPSILON_METERS = 0.001;

/**
 * Whether the ground has been hand-graded — the stored heights say something the generator does not.
 *
 * `Terrain.toProjectState` always bakes heights, so their *presence* proves nothing; only comparing
 * them with what the same parameters generate distinguishes a sculpted site from an untouched one.
 * A length mismatch means the file was written at a different segment count, which is a change to the
 * ground by any reading.
 */
export function hasGroundEdits(state: TerrainProjectState): boolean {
  const baked = state.bakedHeights;
  if (!baked) return false;
  const generated = generateHeightfield(state.generation);
  if (baked.length !== generated.heights.length) return true;
  for (let i = 0; i < baked.length; i += 1) {
    if (Math.abs((baked[i] ?? 0) - (generated.heights[i] ?? 0)) > GRADE_EPSILON_METERS) return true;
  }
  return false;
}
